// Admin lot-state payload — one lot's ABSOLUTE current state, broadcast on the
// obscured admin channel (ADR 042 §§1–2, BL-1251; widened by BL-1554).
//
// This is the payload of the admin lot-state *keyed snapshot* (ADR 011 §2c): on each
// lot-affecting mutation the publisher broadcasts THIS shape for the one
// changed lot (O(1) per mutation), and the client folds it last-writer-wins
// per lot keyed on `sequence`. It is NOT event sourcing — the base table
// `auction_lots` stays the source of truth and the catch-up route re-reads
// current state from it (see `@auctogon/realtime` lot-state catch-up factory).
//
// SCOPE (BL-1554). This carries EVERY field the clerk console renders or sorts
// on that `auction_lots` owns — the live scalars, the ADR-023 decision stamps,
// AND the static display/config fields that used to arrive over a separate REST
// read and be joined by lot id on the client. Folding them removes that second
// delivery path; rule #32 is relaxed here rather than strained.
//
// WHAT DOES NOT RIDE THIS CHANNEL, and why. Two fields the console needs are
// NOT here and must not be added:
//   • `sellerName`      ← `customers.account_name`
//   • `inventoryNumber` ← `inventory_items.inventory_number`
// Both are FAN-OUT relative to this topic: one customer or one inventory item
// maps to lots across many auctions.
// `DerivedSnapshotDependency.resolveParentId` returns ONE parent
// id, so the invalidation edge is literally inexpressible — which is rule #40's
// ban enforced by a type signature rather than by review. They stay on
// `GET /api/auctions/[id]/clerk/catalog`, now narrowed to exactly those two
// plus the join key.
//
// CONFIDENTIAL FIELDS (BL-1252 / BL-1257). `topMaxAmount` and `reserve` are
// clerk-only: they ride ONLY this obscured admin channel and MUST NOT appear on
// any bidder/public snapshot, ledger, or REST payload. BL-1554 REMOVED
// `topMaxCustomerId` — it had no consumer outside a docblock, and shipping a
// bidder's identity on every row of a 5,000-lot seed bought nothing.
//
// BL-1553 removed `watcherCount`; the console fetches that count on lot load
// from AuctionWatchersService instead (ADR 053 — Console Scale). **Do not re-add
// a watcher count here** — it re-creates the second delivery path rule #32
// prevents, and re-opens the churn where every watch by any bidder became an
// auction-wide admin broadcast.
//
// REALTIME-PROJECTION NOTE (denormalized-counts-and-fields.md §5). Three fields
// here are trigger-maintained `*_denormalized` columns on `auction_lots`, each a
// NAMED carve-out from the "never project a `*_denormalized` column into
// realtime" ban:
//   • `accepted_bid_unit_amount_denormalized`   (BL-626)  → acceptedBidAmount
//   • `bids_placed_count_denormalized`     (BL-588)  → bidsPlacedCount
//   • `active_max_bid_amount_denormalized` (BL-1252) → topMaxAmount
// (BL-1553 deleted `lot_watchlist_count_denormalized` outright, and BL-1747 did
// the same for BL-1252's `active_max_bid_customer_id_denormalized` — with
// `topMaxCustomerId` already off this projection per BL-1554, its recompute
// trigger was maintaining a column with zero readers on `max_bids`, the hottest
// write path in the system. Column, trigger and function are all gone.)
// Freshness holds because this publisher fires on the SAME `markMutated('lot')`
// + derived-snapshot-backstop path as `publishLotSnapshot`, so the post-commit
// re-SELECT reads the fresh trigger-maintained values. Ordering no longer rides on
// `updated_at` at all: BL-1628 moved it onto `admin_lot_state_seq.sequence`, claimed
// at the END of the mutating transaction and therefore in COMMIT order.
//
// MONEY (BL-2009, ADR 060 — Money Type § 2.3). The six money fields ride as
// `MoneyWire` — `{ amount: "300.0000", currency: "USD" }` — in the lot row's own
// `auction_lots.currency`, encoded HERE by the shared `encodeRowMoney`, so the
// live publisher and the catch-up route (both call this mapper) emit identical
// bytes (ADR 011 — Durable Events). The client decodes each delta once, at the
// lot store's ingest ({@link decodeAdminLotStateDelta}); no reader pairs an
// amount with a currency from anywhere else.

import type Decimal from 'decimal.js';

import type { MoneyWire } from '../../isomorphic/money/codec';
import type { Money } from '../../isomorphic/money/money';
import type { ReserveBadgeDisplay } from '../../isomorphic/reserve/reserve-badge-display';
import { decodeWireAmount, encodeRowMoney } from './money-wire-amount';

/**
 * Every field this channel projects — the rule #32 seam, in one greppable place.
 *
 * The test it exists for: **if a field is in this list, it belongs on the
 * channel, not in a REST response read by a surface that subscribes here.**
 * Several docblocks around the clerk catalog point at it by name.
 *
 * It is a field LIST rather than the `EMPTY_*` nulled-object other payloads
 * export, because those seed a context that would otherwise sit on `undefined`
 * before catch-up lands. This stream has no such default: the client holds a
 * `Map<lotId, delta>` where a lot that has not arrived is simply absent, and a
 * nulled `lotId`/`sequence` would be meaningless.
 */
export const ADMIN_LOT_STATE_PROJECTED_FIELDS = [
    'lotId',
    'lotNumber',
    'lotNumberSuffix',
    'name',
    'thumbnailUrl',
    'extensionGroupId',
    'ringId',
    'startPrice',
    'increment',
    'acreage',
    'pricingType',
    'reserve',
    'reserveBadgeDisplay',
    'closeTime',
    'originalCloseTime',
    'acceptedBidAmount',
    'bidsPlacedCount',
    'topMaxAmount',
    'hammerPrice',
    'winnerCustomerId',
    'readiedAt',
    'preBiddingAt',
    'biddingOpenedAt',
    'pausedAt',
    'withdrawnAt',
    'hammeredAt',
    'closeProcessedAt',
    'deletedAt',
    'sequence',
] as const;

/**
 * The ADR-023 decision stamps, which are the fields BL-1554 omits from the
 * serialised payload when null. Exported so the publisher, the catch-up route
 * and their tests all agree on the set rather than each listing it.
 */
export const ADMIN_LOT_STATE_STAMP_FIELDS = [
    'readiedAt',
    'preBiddingAt',
    'biddingOpenedAt',
    'pausedAt',
    'withdrawnAt',
    'hammeredAt',
    'closeProcessedAt',
    'deletedAt',
] as const;

/**
 * The six money fields, each a `MoneyWire | null` on the wire and a
 * `Money | null` once decoded (BL-2009). One list, so the mapper's encode and
 * the client's decode cannot disagree on the set.
 */
export const ADMIN_LOT_STATE_MONEY_FIELDS = [
    'startPrice',
    'increment',
    'reserve',
    'acceptedBidAmount',
    'topMaxAmount',
    'hammerPrice',
] as const;

/** One money field's key. */
export type AdminLotStateMoneyField = (typeof ADMIN_LOT_STATE_MONEY_FIELDS)[number];

/** One ADR-023 decision stamp's key. */
export type AdminLotStateStampField = (typeof ADMIN_LOT_STATE_STAMP_FIELDS)[number];

/**
 * One lot's absolute current state on the admin lot-state channel.
 *
 * `sequence` is the lot's position in its AUCTION's monotonic series, claimed from
 * `ledger_topic_seq` and stamped on `admin_lot_state_seq` by the batch finalizer at
 * the END of the mutating transaction — so the series is in COMMIT order, dense per
 * auction, and strictly increasing. It is both the last-writer-wins ordering key the
 * client folds on (highest sequence per lot wins) and the topic-level gap detector.
 *
 * **This replaced an `updated_at` cursor (BL-1628), and the reason is worth keeping.**
 * `set_updated_at()` writes `now()` — transaction-START time — so a lot written by a
 * slow transaction carried a cursor BELOW a client's, and catch-up's `>= cursor`
 * filter could never return it. Not late: never. Absolute-LWW makes a re-delivered
 * message idempotent; it does nothing for a skipped one, and nothing re-sent it. A
 * commit-ordered sequence closes both that skew and the undetectability of a dropped
 * delta, which is why one field does both jobs here.
 *
 * **The eight decision stamps are OPTIONAL** because null keys are omitted from
 * the wire (BL-1554). A lot carries two or three set stamps, not eight, and at
 * 5,000 lots the difference is real. Consumers already use `== null` guards,
 * which read an absent key and a null one identically.
 */
export interface AdminLotStateDelta {
    /** `auction_lots.id`. The fold key. */
    lotId: string;

    // ── Static display + config, folded in by BL-1554 ────────────────────────
    /**
     * `auction_lots.lot_number` — an INTEGER since BL-1200, sent as the raw
     * number rather than a pre-composed display string. The rail SORTS on it,
     * and "12A" as text ties against "12"; the client composes the label from
     * this and {@link AdminLotStateDelta.lotNumberSuffix} at the point of use.
     */
    lotNumber: number | null;
    /**
     * `auction_lots.lot_number_suffix` (`^[A-Z]{0,2}$`), `''` when the lot has
     * none — the column is `NOT NULL DEFAULT ''`, so this is a string, never
     * null, and the delta satisfies `LotNumberPair` structurally.
     *
     * Load-bearing and easy to drop: without it the rail renders "12" for lot
     * "12A" and ties 12 against 12A in every sort.
     */
    lotNumberSuffix: string;
    /** `auction_lots.name_for_auction` — the row's display title. */
    name: string | null;
    /**
     * Absolute URL of the lot's cover still, or `null` for the placeholder:
     * the Main photo, or a Main video's poster, by the shared `deriveCoverUrl`
     * rule (BUG-2039). Built in the publisher and the catch-up via
     * `getInventoryMediaUrlBuilder()` — a deterministic string build with no
     * network call, which is what keeps the mapper synchronous.
     */
    thumbnailUrl: string | null;
    /** `auction_lots.extension_group_id` — `null` when the lot closes alone. */
    extensionGroupId: string | null;
    /**
     * `auction_lots.ring_id` — `null` on an unassigned lot (rings are optional
     * after BL-1166). The ring's ranked POSITION is deliberately not here; see
     * the fan-out note at the top of this file.
     */
    ringId: string | null;
    /** `auction_lots.start_price` in the lot's currency (BL-2009). Ask input. */
    startPrice: MoneyWire | null;
    /** Per-lot increment override in the lot's currency, or `null` to fall through. */
    increment: MoneyWire | null;
    /** `auction_lots.acreage` as a decimal string; `null` on a non-land lot. */
    acreage: string | null;
    /**
     * `auction_lots.pricing_type` (`'per_acre'` / `'total'`), `null` on a non-land
     * lot. Decides whether money on this lot carries a `/ac` suffix (ADR 035).
     *
     * ADR 035 §6 previously recorded this field and `acreage` as compliant-by-
     * construction BECAUSE they were on no snapshot. BL-1554 puts them here,
     * which makes the clerk console's per-acre derivation single-source rather
     * than mixing a live price with an off-snapshot multiplier — a strengthening
     * of that section's goal, not a breach of it. BL-2359 did the same on the
     * bidder side: both fields now ride `lot:{id}:snapshot` too (ADR 035 § 6).
     */
    pricingType: string | null;
    /**
     * BUG-2116 — `auction_lots.reserve_badge_display`, the house pick stamped
     * on the lot (BL-2172). The Update reserve modal offers "Tell bidders" only
     * on a house that shows bidders a reserve, beside the live phase off this
     * same stream (rule #32), never off a REST read.
     */
    reserveBadgeDisplay: ReserveBadgeDisplay;

    // ── Live scalars ─────────────────────────────────────────────────────────
    /** `auction_lots.close_time` — scheduled close; mutates on anti-snipe extend. */
    closeTime: string | null;
    /** `auction_lots.original_close_time` — pre-extension close (delta source). */
    originalCloseTime: string | null;
    /**
     * `auction_lots.accepted_bid_unit_amount_denormalized` (BL-626) — the lot's live
     * highest accepted bid. `null` = no accepted bid yet; consumers MUST use
     * `value == null` guards, never truthiness. Named realtime carve-out.
     */
    acceptedBidAmount: MoneyWire | null;
    /**
     * `auction_lots.bids_placed_count_denormalized` (BL-588) — placed-bid count.
     * `0` is valid and is exactly what a clerk scans for; consumers MUST use
     * `value > 0` guards. Named realtime carve-out.
     */
    bidsPlacedCount: number;
    /**
     * `auction_lots.active_max_bid_amount_denormalized` (BL-1252) — the lot's
     * current highest in-force proxy ceiling. `null` = no active max.
     * CONFIDENTIAL — admin channel only.
     */
    topMaxAmount: MoneyWire | null;
    /**
     * `auction_lots.reserve` (BL-1257) — the seller's reserve as a MoneyWire,
     * `null` when the lot carries none (the common case). CONFIDENTIAL —
     * admin channel only; the public `lot_snapshot` never carries the amount
     * (BUG-1910) — only the house's opt-in `reserveBadge` word (BL-2169 /
     * BL-2203), computed server-side.
     *
     * `null` is load-bearing and is NOT omitted like the stamps are: a `?? 0`
     * here turns every unreserved lot into a $0 reserve that every bid "meets",
     * and an absent key would be indistinguishable from "not yet delivered".
     */
    reserve: MoneyWire | null;
    /**
     * The RECORDED SALE price (`soldHammerAmountSql`, BL-1814) as a MoneyWire,
     * `null` for a lot's entire live phase (BL-1542 AC 36). CONFIDENTIAL — admin
     * channel only.
     *
     * DELIBERATELY NOT ON THE PUBLIC `lot_snapshot`. BL-1519 removed
     * `hammerPriceDeprecated` from that payload and left a standing "Do NOT
     * re-add it" — a sold lot's price belongs to the surfaces that own post-sale
     * money, and a second live path there is what rule #32 exists to prevent.
     * That decision is untouched; this is the CLERK channel, where the bid
     * record's sold strip is the one consumer and there is no second path.
     *
     * `null` is load-bearing and is NOT omitted like the stamps are: absent
     * would be indistinguishable from "not yet delivered", and a `?? 0` would
     * paint every unsold lot as having sold for nothing.
     */
    hammerPrice: MoneyWire | null;
    /**
     * `auction_lots.winner_customer_id` — the settled buyer's `customers.id`,
     * `null` until the lot is hammered. CONFIDENTIAL — admin channel only.
     *
     * NOT the same thing as the public snapshot's `currentBidderCustomerId`,
     * which is the standing HIGH BIDDER — a live position that changes with
     * every bid. The winner is the recorded outcome and survives a buyer swap;
     * aliasing one to the other would put a subtly wrong value behind a
     * right-looking name.
     *
     * BL-645 deliberately kept this off `lot_snapshot` because that payload
     * rides the public/anon bidder channel and this is buyer identity. That
     * holds; the admin channel already carries `reserve` and `topMaxAmount`,
     * which are strictly more sensitive.
     */
    winnerCustomerId: string | null;

    // ── ADR-023 decision stamps — OPTIONAL; null keys omitted on the wire ─────
    readiedAt?: string | null;
    preBiddingAt?: string | null;
    biddingOpenedAt?: string | null;
    pausedAt?: string | null;
    withdrawnAt?: string | null;
    hammeredAt?: string | null;
    // NOTE: `passedAt` was retired by BL-1387 PR 2 — the `passed` lot phase token
    // and the `auction_lots.passed_at` column are gone. A no-sale is recorded as
    // `disposition = 'passed'` alongside `closeProcessedAt`.
    closeProcessedAt?: string | null;
    deletedAt?: string | null;

    /**
     * The lot's position in its AUCTION's monotonic sequence, from
     * `admin_lot_state_seq.sequence` (BL-1628).
     *
     * Does double duty, which is the point: it is the last-writer-wins ordering key
     * the client folds on (highest sequence per lot wins) AND the topic-level gap
     * detector (a client holding N applies N+1 directly and catches up on N+2).
     */
    sequence: number;
}

/**
 * Input row shape — duck-typed to the Drizzle SELECT that the publisher and the
 * catch-up factory BOTH run against `auction_lots`. Both build this explicitly
 * annotated shape and hand it to {@link mapAdminLotState}, which is what makes a
 * field missing from either SELECT a compile error rather than a silent drift.
 *
 * Unlike the delta, every stamp here is REQUIRED — this is the read shape, and
 * omission happens in the mapper, once, for both callers.
 *
 * `sequence` is read in SQL from the joined `admin_lot_state_seq` row, so the mapper
 * stays a pure reshape with no ordering logic of its own.
 */
export interface AdminLotStateRow {
    lotId: string;
    lotNumber: number | null;
    lotNumberSuffix: string;
    name: string | null;
    thumbnailUrl: string | null;
    extensionGroupId: string | null;
    ringId: string | null;
    /** `auction_lots.currency` — the lot's own ISO 4217 code, which every money
     *  field below is in (BL-2009). Not projected on its own: it rides inside
     *  each field's MoneyWire. */
    currency: string;
    /** `moneyNumeric` (BL-2239) — arrives as a `Decimal`. */
    startPrice: Decimal | null;
    /** `moneyNumeric` (BL-2239) — arrives as a `Decimal`. */
    increment: Decimal | null;
    acreage: string | null;
    pricingType: string | null;
    reserveBadgeDisplay: ReserveBadgeDisplay;
    closeTime: string | null;
    originalCloseTime: string | null;
    /** `moneyNumeric` (BL-1941) — arrives as a `Decimal`. */
    acceptedBidAmount: Decimal | null;
    bidsPlacedCount: number;
    /** `moneyNumeric` (BL-1941) — arrives as a `Decimal`. */
    topMaxAmount: Decimal | null;
    /** `moneyNumeric` (BL-2239) — arrives as a `Decimal`. */
    reserve: Decimal | null;
    /** `soldHammerAmountSql` (raw SQL, BL-1814) — arrives as a decimal string. */
    hammerPrice: string | null;
    /** `auction_lots.winner_customer_id` → `winnerCustomerId` (BL-1542). */
    winnerCustomerId: string | null;
    readiedAt: string | null;
    preBiddingAt: string | null;
    biddingOpenedAt: string | null;
    pausedAt: string | null;
    withdrawnAt: string | null;
    hammeredAt: string | null;
    closeProcessedAt: string | null;
    deletedAt: string | null;
    sequence: number;
}

/**
 * Map an `auction_lots` source row to the admin lot-state delta shape.
 *
 * Pure reshape — no I/O, no date math (the caller reads `sequence` in SQL) —
 * with TWO transformations: a decision stamp whose value is `null` is omitted
 * from the result entirely (BL-1554 AC 1a), and each money field is encoded as
 * a MoneyWire in the row's currency (BL-2009).
 *
 * **The omission lives here, not at the call sites, and that is load-bearing.**
 * The client's fold is `map.set(key, delta)` — a whole-entry REPLACEMENT with no
 * merge — so if the live publisher and the catch-up route omitted differently, a
 * lot's stamp would alternate between `null` and `undefined` depending on which
 * path last delivered it, and `AdminLotStateRow` could not catch it (the row
 * type governs the mapper's INPUT, and omission happens after). One function,
 * two callers, divergence impossible by construction.
 *
 * Non-stamp nulls are preserved verbatim: `reserve`, `acceptedBidAmount`,
 * `topMaxAmount` and the static fields all keep an explicit `null`, which is a
 * different claim from "absent".
 *
 * @param row - The `auction_lots` read shape both realtime paths produce.
 * @returns The wire payload, with null decision stamps omitted.
 * @throws Error when the row's currency is not supported (`encodeRowMoney`):
 *   the publisher's per-auction catch logs it, the catch-up route 500s.
 * @sideEffect none
 */
export function mapAdminLotState(row: AdminLotStateRow): AdminLotStateDelta {
    const delta: AdminLotStateDelta = {
        lotId: row.lotId,
        lotNumber: row.lotNumber,
        lotNumberSuffix: row.lotNumberSuffix,
        name: row.name,
        thumbnailUrl: row.thumbnailUrl,
        extensionGroupId: row.extensionGroupId,
        ringId: row.ringId,
        startPrice: encodeLotMoney(row.startPrice, row.currency),
        increment: encodeLotMoney(row.increment, row.currency),
        acreage: row.acreage,
        pricingType: row.pricingType,
        reserveBadgeDisplay: row.reserveBadgeDisplay,
        closeTime: row.closeTime,
        originalCloseTime: row.originalCloseTime,
        acceptedBidAmount: encodeLotMoney(row.acceptedBidAmount, row.currency),
        bidsPlacedCount: row.bidsPlacedCount,
        topMaxAmount: encodeLotMoney(row.topMaxAmount, row.currency),
        // No `?? 0`: a null reserve means "no reserve", and coercing it to zero
        // would paint a met badge on every unreserved lot.
        reserve: encodeLotMoney(row.reserve, row.currency),
        // Same reasoning, same shape: null means "not sold", never "$0".
        hammerPrice: encodeLotMoney(row.hammerPrice, row.currency),
        winnerCustomerId: row.winnerCustomerId,
        sequence: row.sequence,
    };

    for (const stamp of ADMIN_LOT_STATE_STAMP_FIELDS) {
        const value = row[stamp];
        if (value != null) delta[stamp] = value;
    }

    return delta;
}

/** Encode one nullable amount in the lot's currency; `null` stays `null`. */
function encodeLotMoney(amount: Decimal | string | null, currency: string): MoneyWire | null {
    return amount == null ? null : encodeRowMoney({ amount, currency });
}

/**
 * A lot-state delta as the client holds it: every money field decoded into a
 * `Money` once, at the lot store's ingest (BL-2009), so no component, selector
 * or derivation re-parses an amount or pairs it with a currency from elsewhere.
 */
export type AdminLotStateDeltaDecoded = Omit<AdminLotStateDelta, AdminLotStateMoneyField> & {
    [K in AdminLotStateMoneyField]: Money | null;
};

/** How one lot-state delta decoded. */
export type AdminLotStateDeltaDecodeResult =
    | { kind: 'ok'; delta: AdminLotStateDeltaDecoded }
    /** The field that was not a well-formed MoneyWire — logged and skipped by the caller. */
    | { kind: 'undecodable'; field: AdminLotStateMoneyField };

/**
 * Decode one lot-state delta's six money fields (BL-2009). `null` stays `null`.
 *
 * @param delta - The delta as it arrived (broadcast or catch-up).
 * @returns The decoded delta, or `undecodable` naming the first malformed field.
 *   Never throws: the caller logs and skips the delta.
 */
export function decodeAdminLotStateDelta(delta: AdminLotStateDelta): AdminLotStateDeltaDecodeResult {
    const money = {} as { [K in AdminLotStateMoneyField]: Money | null };
    for (const field of ADMIN_LOT_STATE_MONEY_FIELDS) {
        const wire = delta[field];
        if (wire == null) {
            money[field] = null;
            continue;
        }
        const decoded = decodeWireAmount(wire);
        if (decoded.kind !== 'money') return { kind: 'undecodable', field };
        money[field] = decoded.money;
    }
    return { kind: 'ok', delta: { ...delta, ...money } };
}

/**
 * Result of the admin lot-state catch-up routes (`createLotStateCatchupHandler`):
 * the minted obscured `topic`, the current state of every changed lot, and the max
 * `sequence` in that set (the client's next `since_sequence`). Declared beside the
 * delta so the route registry can type the response (ADR 027 — Response
 * Contract) without importing the client store (BL-2009).
 */
export interface LotStateCatchupResult {
    topic: string;
    lots: AdminLotStateDelta[];
    sequence: number;
    /**
     * How many live (not deleted) lots the scope holds right now — the auction's
     * catalog, or 0 / 1 for a lot scope (BUG-1766).
     *
     * A reconcile only MERGES, so it can never name a lot that left the auction. The
     * client's live lots are therefore always a superset of the server's, and holding
     * MORE than this count means it holds a lot whose departure frame it missed: the
     * store reloads and replaces its list. A count, not the id list, so a reconcile
     * stays O(changed) at 5,000 lots (ADR 053 — Console Scale).
     */
    liveLotCount: number;
}
